import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useLogs } from "../../providers/LogProvider";
import { AutoComplete, Button, Flex, Input, Modal, Typography } from "antd";
import dayjs, { ManipulateType } from "dayjs";
import { DIRECTIONS } from "../../api/types";
import utc from "dayjs/plugin/utc";
import { TimePickers } from "../../components/TimePickers";
import { ActiveFilters } from "../../components/ActiveFilters";
import { LiveTail } from "../../components/LiveTail";
import { ClearAllFilters } from "../../components/ClearAllFilters";
import { Table } from "../../components/LogTable/Table";
import { WorkerFilter } from "../../components/WorkerFilter";
import { LogSettings } from "../../components/LogSettings";
import { InfoCircleOutlined, SyncOutlined } from "@ant-design/icons";

const { Search } = Input;

dayjs.extend(utc);

export const Logs = () => {
  const {
    getLogs,
    logs,
    activeApp,
    apps,
    searchPhrase,
    filters,
    setFilters,
    liveTail,
    resetLogs,
    highlightedLog,
    setHighlightedLog,
    responseTime,
    requestTime,
    setIsLoading,
    getHistory,
    history,
    logsMeta,
  } = useLogs();
  const [showHint, setShowHint] = useState<boolean>(false);
  const [sp, setSearchParams] = useSearchParams();
  const [tempSearch, setTempSearch] = useState<string>("");

  if (!apps) {
    return null;
  }

  useEffect(() => {
    if (logs.length === 0) {
      setIsLoading(true);
    }
  }, []);

  useEffect(() => {
    if (activeApp) {
      getHistory();
    }
  }, [activeApp]);

  useEffect(() => {
    if (searchPhrase?.length === 0 || searchPhrase === null) {
      setTempSearch("");
    }
  }, [searchPhrase]);

  useEffect(() => {
    if (
      filters &&
      filters.timestamp === "yes" &&
      highlightedLog &&
      logs.length > 0 &&
      responseTime > requestTime
    ) {
      document.querySelector(`#${highlightedLog}`)?.scrollIntoView();
    }
  }, [logs, highlightedLog, filters]);

  useEffect(() => {
    if (filters === null) {
      const currFilters: any = {};
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.size === 0 && apps.length > 0) {
        const now = new Date();
        currFilters.dateFrom = now.getTime().toString();
        currFilters.dateTo = now.getTime().toString();
        currFilters.direction = DIRECTIONS.before;
        if (!currFilters.activeApp) {
          if (localStorage.getItem("appId")) {
            const app = apps.find(
              (app) => app.name === localStorage.getItem("appId"),
            );
            if (app) {
              currFilters.activeApp = app.id;
            }
          } else {
            currFilters.activeApp = apps[0].id;
          }
        }
        setFilters(currFilters);
        return;
      }
      for (const [key, value] of searchParams.entries()) {
        if (key === "worker") {
          //check if currFilters has key worker, if not assign empty array and push value, otherwise push value to existing array
          if (currFilters[key]) {
            currFilters[key].push(value);
          } else {
            currFilters[key] = [value];
          }
          continue;
        }
        if (key === "text") {
          currFilters.direction = DIRECTIONS.middle;
          setTempSearch(value);
        }
        // eslint-disable-next-line
        // @ts-ignore
        currFilters[key] = value;
      }

      setFilters(currFilters);
    }
  }, [filters]);

  useEffect(() => {
    const token = localStorage.getItem("token") || null;
    if (!token || filters === null || apps.length === 0) {
      return;
    }

    if (filters.timestamp !== "yes" && highlightedLog) {
      setHighlightedLog(null);
    }
    setSearchParams(filters);
    const dateFrom = filters.dateFrom
      ? new Date(parseInt(filters.dateFrom))
      : new Date();

    const dateTo = filters.dateTo
      ? new Date(parseInt(filters.dateTo))
      : new Date();
    const direction = filters.direction || DIRECTIONS.middle;
    getLogs({
      appId: filters.activeApp,
      text: filters.text || null,
      dateFrom: dateFrom.toISOString(),
      dateTo: dateTo.toISOString(),
      direction,
      worker: filters.worker || [],
    });
    if (tempSearch.length === 0 && filters.text) {
      setTempSearch(filters.text);
    }
    if (tempSearch.length > 0 && !filters.text) {
      setTempSearch("");
    }
  }, [filters]);

  const handleSearchPhraseChange = (phrase: string) => {
    setTempSearch(phrase);
    if (!filters) {
      return;
    }
    const activeFilters = { ...filters };
    if (activeFilters.dateFrom === activeFilters.dateTo && phrase.length > 0) {
      activeFilters.dateFrom = dayjs().subtract(2, "h").valueOf().toString();
      activeFilters.dateTo = dayjs().valueOf().toString();
      activeFilters.relativeTime = "2#h";
      activeFilters.direction = DIRECTIONS.middle;
    }

    if (activeFilters.relativeTime && phrase.length > 0) {
      const now = dayjs();
      const relativeUnits = activeFilters.relativeTime.split("#");
      activeFilters.dateFrom = now
        .subtract(
          parseInt(relativeUnits[0]),
          relativeUnits[1] as dayjs.ManipulateType | undefined,
        )
        .valueOf()
        .toString();
      activeFilters.dateTo = now.valueOf().toString();
    }
    if (liveTail) {
      resetLogs();
    }
    if (filters.timestamp === "yes") {
      delete activeFilters["timestamp"];
    }
    setFilters({ ...activeFilters, text: phrase });
  };

  const refreshLogs = () => {
    if (!filters) {
      return;
    }

    if (filters.relativeTime) {
      const time = filters.relativeTime.split("#");
      setFilters({
        ...filters,
        dateFrom: dayjs()
          .subtract(parseInt(time[0]), time[1] as ManipulateType)
          .valueOf()
          .toString(),
        dateTo: new Date().getTime().toString(),
      });
      return;
    }

    setFilters({
      ...filters,
      dateFrom: new Date().getTime().toString(),
      dateTo: new Date().getTime().toString(),
    });
  };

  const showPrevLogs = () => {
    if (!filters) {
      return;
    }
    setFilters({
      ...filters,
      direction: DIRECTIONS.before,
      dateFrom: new Date(logs[0].date).getTime().toString(),
      dateTo: new Date(logs[0].date).getTime().toString(),
    });
  };

  const showNextLogs = () => {
    if (!filters) {
      return;
    }

    setFilters({
      ...filters,
      direction: DIRECTIONS.after,
      dateFrom: new Date(logs[logs.length - 1].date).getTime().toString(),
      dateTo: new Date(logs[logs.length - 1].date).getTime().toString(),
    });
  };

  return (
    <>
      <Flex justify="space-between" align="center" style={{ margin: "20px 0" }}>
        <Flex
          align="center"
          style={{ position: "relative", width: "calc(100% - 310px)" }}
        >
          <Button
            onClick={() => setShowHint(true)}
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "10px",
            }}
          >
            <InfoCircleOutlined />
          </Button>
          <AutoComplete
            style={{ width: "100%" }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={null}
            options={(history || []).map((d) => {
              return {
                value: d.text,
                label: (
                  <Flex justify="space-between">
                    <div>{d.text}</div>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      {dayjs(d.timestamp).format("YYYY-MM-DD HH:mm:ss")}
                    </Typography.Text>
                  </Flex>
                ),
              };
            })}
            onSelect={(val) => [
              setTempSearch(val),
              handleSearchPhraseChange(val),
            ]}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearchPhraseChange(tempSearch);
              }
            }}
            onChange={(e) => setTempSearch(e)}
            value={tempSearch}
          >
            <Search
              placeholder="Filter logs - press enter to search"
              allowClear
              onSearch={(value) => handleSearchPhraseChange(value)}
            />
          </AutoComplete>
        </Flex>
        <TimePickers key={activeApp} />
      </Flex>
      <Flex
        justify="flex-start"
        align="center"
        style={{ marginBottom: "10px" }}
      >
        <Button
          disabled={filters?.timestamp === "yes"}
          onClick={refreshLogs}
          style={{ display: "flex", alignItems: "center", marginRight: "10px" }}
        >
          <SyncOutlined />
        </Button>
        <LiveTail />
        <WorkerFilter />
        <LogSettings />
      </Flex>

      {logsMeta && logsMeta.total > 500 && (
        <Flex style={{ margin: "15px 0 10px" }}>
          <Typography.Text style={{ color: "white" }}>
            There are {logsMeta.total >= 10000 ? "over 10000" : logsMeta.total}{" "}
            results that match your query, but we are only showing 500. Please
            refine your search or create an{" "}
            <Link
              to={"/exports"}
              style={{ color: "white", textDecoration: "underline" }}
            >
              export
            </Link>
            .
          </Typography.Text>
        </Flex>
      )}

      <Flex
        justify="space-between"
        align="center"
        style={{ marginBottom: "10px" }}
      >
        <ActiveFilters />
        <ClearAllFilters />
      </Flex>

      <Table logs={logs} />
      {showHint && (
        <Modal
          title="Search tips"
          open
          onCancel={() => setShowHint(false)}
          footer={null}
        >
          <ul style={{ listStyleType: "disc" }}>
            <li style={{ margin: "10px 0 0 15px" }}>
              Use <strong>AND</strong> to find results that contain both
              phrases. For example:
              <br />
              <strong>"apple juice" AND "orange"</strong> <br />
              This will match lines that contain both "apple juice" and
              "orange," with any text in between.
            </li>
            <li style={{ margin: "10px 0 0 15px" }}>
              Use <strong>OR</strong> to find results that contain either
              phrase. For example: <br />
              <strong>"apple juice" OR "orange"</strong> <br />
              This will match lines that contain either "apple juice" (exactly
              as a phrase) or the keyword "orange."
            </li>
            <li style={{ margin: "10px 0 0 15px" }}>
              Enclose phrases in <strong>double quotes</strong> to search for
              exact matches. For example: <br />
              <strong>"apple juice"</strong> will only match lines where the
              words appear next to each other.
            </li>
            <li style={{ margin: "10px 0 0 15px" }}>
              To exclude a phrase from your search results, use a{" "}
              <strong>- (minus sign)</strong> in front of the phrase. <br />
              For example, searching for <strong>"apple" -"banana"</strong> will
              return results that contain "apple" but do not contain "banana".
            </li>
          </ul>
        </Modal>
      )}
    </>
  );
};
